// src/components/Overview.js
import React, { useState, useEffect } from 'react';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { db } from '../firebase';
import { collection, getDocs, setDoc, doc, onSnapshot, getDoc, query, where, orderBy, limit } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

function Overview() {
  const [predictionCounts, setPredictionCounts] = useState({
    xrayPneumonia: 0,
    xrayNormal: 0,
    brainGlioma: 0,
    brainMeningioma: 0,
    brainPituitary: 0,
    brainNoTumor: 0,
  });
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const recordsPerPage = 10;
  const navigate = useNavigate();
  const auth = getAuth();

  const handleBuyCredits = () => {
    navigate('/dashboard/plans');
  };

  // Check authentication state and fetch user profile
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const userRef = doc(db, 'users', currentUser.uid);
        try {
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            setProfile(userSnap.data());
          } else {
            console.warn('User profile not found, creating default...');
            await setDoc(userRef, {
              uid: currentUser.uid,
              email: currentUser.email,
              displayName: currentUser.displayName || 'User',
              credits: 100,
              createdAt: new Date().toISOString(),
            });
            setProfile({ uid: currentUser.uid, email: currentUser.email, displayName: 'User', credits: 100 });
          }
        } catch (err) {
          console.error('Error fetching user profile:', err);
        }
      } else {
        navigate('/');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  // Fetch and listen to prediction counts
  useEffect(() => {
    if (!user) {
      return;
    }

    const countsRef = doc(db, 'prediction_counts', 'counts');
    const initializeCounts = async () => {
      try {
        const countsSnap = await getDoc(countsRef);
        if (!countsSnap.exists()) {
          await setDoc(countsRef, {
            xrayPneumonia: 0,
            xrayNormal: 0,
            brainGlioma: 0,
            brainMeningioma: 0,
            brainPituitary: 0,
            brainNoTumor: 0,
          });
          setPredictionCounts({
            xrayPneumonia: 0,
            xrayNormal: 0,
            brainGlioma: 0,
            brainMeningioma: 0,
            brainPituitary: 0,
            brainNoTumor: 0,
          });
        } else {
          setPredictionCounts(countsSnap.data());
        }

        const unsubscribe = onSnapshot(countsRef, (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data();
            setPredictionCounts(data);
          } else {
            console.warn('Counts document does not exist');
          }
        }, (error) => {
          console.error('Error listening to counts:', error);
        });

        return () => unsubscribe();
      } catch (err) {
        console.error('Error initializing counts:', err);
      }
    };

    initializeCounts();
  }, [user]);

  // Fetch prediction history
  useEffect(() => {
    const fetchHistory = async () => {
      if (!user) {
        return;
      }
      setLoading(true);
      try {
        // Fetch paginated records
        const q = query(
          collection(db, 'prediction_history'),
          where('userId', '==', user.uid),
          orderBy('date', 'desc'),
          limit(recordsPerPage)
        );

        const querySnapshot = await getDocs(q);
        const historyData = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          let status;
          
          if (data.type === 'Brain MRI') {
            // For Brain MRI, use the prediction directly with first letter capitalized
            status = data.result ? data.result.charAt(0).toUpperCase() + data.result.slice(1) : 'Unknown';
          } else {
            // For X-ray, use the old mapping
            status = data.result === 'Pneumonia detected' ? 'Pneumonia' : 'Normal';
          }
          
          return {
            id: doc.id,
            name: data.labelName || 'Anonymous',
            type: data.type || 'Chest Dx',
            status,
            date: data.date ? new Date(data.date).toLocaleString() : 'Unknown Date',
          };
        });

        // Sort history by date (newest first)
        const sortedHistory = historyData.sort((a, b) => new Date(b.date) - new Date(a.date));
        setHistory(sortedHistory);
      } catch (err) {
        console.error('Error fetching history:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [user]);

  // Chart Data for X-ray Predictions (Bar Chart)
  const xrayData = {
    labels: ['Pneumonia', 'Normal'],
    datasets: [{
      label: 'X-ray Prediction Requests',
      data: [predictionCounts.xrayPneumonia, predictionCounts.xrayNormal],
      backgroundColor: ['rgba(75, 192, 192, 0.6)', 'rgba(54, 162, 235, 0.6)'],
      borderColor: ['rgba(75, 192, 192, 1)', 'rgba(54, 162, 235, 1)'],
      borderWidth: 1,
    }],
  };

  // Chart Data for Brain Tumor MRI Predictions (Pie Chart)
  const mriData = {
    labels: ['Glioma', 'Meningioma', 'Pituitary', 'No Tumor'],
    datasets: [{
      data: [
        predictionCounts.brainGlioma,
        predictionCounts.brainMeningioma,
        predictionCounts.brainPituitary,
        predictionCounts.brainNoTumor,
      ],
      backgroundColor: ['rgba(255, 99, 132, 0.6)', 'rgba(255, 206, 86, 0.6)', 'rgba(75, 192, 192, 0.6)', 'rgba(153, 102, 255, 0.6)'],
    }],
  };

  // Improved chart options for better readability
  const chartOptions = {
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: 14,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.label}: ${context.raw}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          font: {
            size: 12,
          },
        },
      },
      x: {
        ticks: {
          font: {
            size: 12,
          },
        },
      },
    },
    maintainAspectRatio: false,
  };

  if (loading) return <div>Loading...</div>;
  if (!user || !profile) return <div>Please log in to view the dashboard.</div>;

  return (
    <div className="min-h-screen font-['Noto Sans'] pt-12">
      <div className="overview p-6 font-['Noto Sans']">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-4xl font-bold text-gray-600">Overview</h2>
          <button
            onClick={handleBuyCredits}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors duration-300 font-semibold"
          >
            Buy Credits
          </button>
        </div>

        {/* User Profile Section */}
        <div className="relative bg-white bg-opacity-90 rounded-xl shadow-lg p-6 mb-6 border border-gray-300 backdrop-blur-lg overflow-hidden">
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center opacity-90"
            style={{ backgroundImage: 'url(https://hilyoon.com/wp-content/uploads/2025/03/skeleton_resized.png)' }}
          ></div>

          {/* Profile Content */}
          <div className="relative z-10 text-white">
            <h3 className="text-3xl font-semibold mb-5 text-white border-b border-white pb-2">
              My Profile
            </h3>
            <div className="space-y-3">
              <p className="text-lg flex items-center">
                <strong className="mr-2 text-white">Name:</strong> {profile.displayName}
              </p>
              <p className="text-lg flex items-center">
                <strong className="mr-2 text-white">Email:</strong> {profile.email}
              </p>
              <p className="text-lg flex items-center">
                <strong className="mr-2 text-white">Credits:</strong> {profile.credits}
              </p>
              <p className="text-lg flex items-center">
                <strong className="mr-2 text-white">Plan:</strong> {profile.plan}
              </p>
            </div>
          </div>
        </div>

        {/* Two-Sided Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* X-ray Side (Pneumonia/Normal) */}
          <div className="bg-white rounded-lg shadow-md p-6 border-t border-b border-gray-300">
            <h3 className="text-3xl font-bold mb-4 text-gray-600">X-ray Predictions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
                <p className="text-gray-600">Pneumonia</p>
                <h3 className="text-2xl font-bold text-purple-600">{predictionCounts.xrayPneumonia}</h3>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
                <p className="text-gray-600">Normal</p>
                <h3 className="text-2xl font-bold text-green-600">{predictionCounts.xrayNormal}</h3>
              </div>
            </div>
            <div className="h-64">
              <Bar data={xrayData} options={chartOptions} />
            </div>
          </div>

          {/* MRI Side (Brain Tumors) */}
          <div className="bg-white rounded-lg shadow-md p-6 border-t border-b border-gray-300">
            <h3 className="text-3xl font-bold mb-4 text-gray-600">MRI Brain Tumor Predictions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
                <p className="text-gray-600">Glioma</p>
                <h3 className="text-2xl font-bold text-blue-600">{predictionCounts.brainGlioma}</h3>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
                <p className="text-gray-600">Meningioma</p>
                <h3 className="text-2xl font-bold text-orange-600">{predictionCounts.brainMeningioma}</h3>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
                <p className="text-gray-600">Pituitary</p>
                <h3 className="text-2xl font-bold text-red-600">{predictionCounts.brainPituitary}</h3>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
                <p className="text-gray-600">No Tumor</p>
                <h3 className="text-2xl font-bold text-teal-600">{predictionCounts.brainNoTumor}</h3>
              </div>
            </div>
            <div className="h-64">
              <Pie data={mriData} options={chartOptions} />
            </div>
          </div>
        </div>

        {/* My Latest Predictions */}
        <div className="mt-8 bg-white bg-opacity-90 rounded-xl shadow-lg p-6 border border-gray-300 backdrop-blur-lg overflow-hidden">
          <h3 className="text-3xl font-semibold mb-5 text-gray-700 border-b pb-2">
            My Latest Predictions
          </h3>

          {history.length === 0 ? (
            <p className="text-gray-500 text-center">No prediction history available.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-300 rounded-lg shadow-sm">
                <thead>
                  <tr className="bg-gray-100 text-gray-700 text-left">
                    <th className="py-3 px-5 border-b font-semibold">Name</th>
                    <th className="py-3 px-5 border-b font-semibold">Type</th>
                    <th className="py-3 px-5 border-b font-semibold">Status</th>
                    <th className="py-3 px-5 border-b font-semibold">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {history
                    .slice() // Create a copy to avoid mutating the original array
                    .sort((a, b) => new Date(b.date) - new Date(a.date)) // Sort by date (newest first)
                    .slice(0, 5) // Take the 5 most recent
                    .map((record, index) => (
                      <tr key={record.id || index} className="hover:bg-gray-50 transition duration-200">
                        <td className="py-3 px-5 border-b">{record.labelName || record.name || record.data?.labelName || "No Name"}</td>
                        <td className="py-3 px-5 border-b">{record.type}</td>
                        <td className={`py-3 px-5 border-b font-semibold ${record.status === 'Completed' ? 'text-green-600' : 'text-red-600'}`}>
                          {record.status}
                        </td>
                        <td className="py-3 px-5 border-b">{record.date}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default Overview;